import { useHistory } from "react-router";
import { Info, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { CreditsInfo, Subscription as SubscriptionData } from "data/models";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { toTitleCase } from "utils/commons";
import { formatInterval } from "utils/format";
import { Label } from "@/components/ui/label";

interface CurrentPlanCardProps {
  subscription: SubscriptionData;
  credits?: CreditsInfo;
  hasScheduledUpgradeOrDowngrade: boolean;
  hasScheduledCancellation: boolean;
}

export const CurrentPlanCard = ({
  subscription,
  credits,
  hasScheduledUpgradeOrDowngrade,
  hasScheduledCancellation,
}: CurrentPlanCardProps) => {
  const history = useHistory();
  const { isPermitted } = usePermissions();

  const active = subscription.activeSubscription;

  if (!active) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Current Plan: None</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="warning">
            <TriangleAlert />
            <AlertDescription>
              Hub is better with a sub, subscribe to get more out of Hub.
            </AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter className="flex justify-end">
          <PermissionTooltipButton
            permissionKeys={[PermissionsKeys.subscriptionUpdate]}
            additionalDisabled={
              hasScheduledUpgradeOrDowngrade || hasScheduledCancellation
            }
            onClick={() => history.push(Routes.orgManageSubscription)}
          >
            Manage Subscription
          </PermissionTooltipButton>
        </CardFooter>
      </Card>
    );
  }

  if (!credits) return null;

  const overageEnabled = credits.isOverageEnabled;
  const uncappedOverage =
    overageEnabled && (active.isMyob || active.plan.overage_unlimited);
  const planLimit = credits.subCreditsLimit;
  const planSpent = Math.min(credits.subCreditsSpent, planLimit);
  const rawOverageSpent = overageEnabled ? credits.overageCreditsSpent : 0;
  const cappedOverageLimit = overageEnabled ? credits.overageCreditsLimit : 0;
  const cappedOverageSpent = Math.min(rawOverageSpent, cappedOverageLimit);

  const inOverage = overageEnabled && credits.subCreditsSpent >= planLimit;

  // Denominator for the bar:
  // - Stripe bounded: plan + overage limit (fixed, shows progress to hard cap)
  // - Uncapped (MYOB / enterprise): plan limit until in overage, then plan + overage spent (grows)
  const denominator = uncappedOverage
    ? inOverage
      ? planLimit + rawOverageSpent
      : planLimit
    : planLimit + cappedOverageLimit;

  const overageSpentForBar = uncappedOverage
    ? rawOverageSpent
    : cappedOverageSpent;
  const totalSpent = planSpent + overageSpentForBar;

  const planSegmentPct = denominator > 0 ? (planSpent / denominator) * 100 : 0;
  const planTrackPct =
    denominator > 0 ? ((planLimit - planSpent) / denominator) * 100 : 0;
  const overageSegmentPct =
    denominator > 0 ? (overageSpentForBar / denominator) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {toTitleCase(active.plan.product_display_name)}
          {active.trialType ? " (Trial)" : ""}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-muted-foreground">Term</Label>
          <p className="text-base">{formatInterval(active.plan.cadence)}</p>
        </div>
        <div>
          <Label className="text-muted-foreground">Renewal Date</Label>
          <p className="text-base">
            {formatTimestamp(active.renewalDate * 1000)}
          </p>
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <Label className="text-muted-foreground">Credit Usage</Label>
            <p className="text-2xl font-medium whitespace-nowrap">
              {uncappedOverage
                ? `${totalSpent.toLocaleString()} credits used`
                : `${totalSpent.toLocaleString()}/${(
                    planLimit + cappedOverageLimit
                  ).toLocaleString()} credits`}
            </p>
          </div>
          {overageEnabled ? (
            <>
              <div
                className="bg-muted relative flex h-2 w-full overflow-hidden rounded-md"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={denominator}
                aria-valuenow={totalSpent}
                aria-label="Credit usage including overage"
              >
                <div
                  className="bg-primary h-full"
                  style={{ width: `${planSegmentPct}%` }}
                />
                <div className="h-full" style={{ width: `${planTrackPct}%` }} />
                <div
                  className="bg-warning h-full"
                  style={{ width: `${overageSegmentPct}%` }}
                />
              </div>
              <div className="text-muted-foreground text-base mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5">
                  <span className="bg-primary inline-block h-2 w-2 rounded-sm" />
                  Plan: {planSpent.toLocaleString()}/
                  {planLimit.toLocaleString()}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="bg-warning inline-block h-2 w-2 rounded-sm" />
                  {uncappedOverage
                    ? `Overage: ${rawOverageSpent.toLocaleString()} Credits`
                    : `Overage: ${cappedOverageSpent.toLocaleString()}/${cappedOverageLimit.toLocaleString()}`}
                </span>
              </div>
              {inOverage && (
                <p className="text-muted-foreground mt-2 text-sm">
                  {uncappedOverage
                    ? "Plan credits exhausted — additional usage handled per your contract."
                    : "Plan credits exhausted — additional usage is billed as overage."}
                </p>
              )}
            </>
          ) : (
            <Progress
              value={
                planLimit > 0 ? Math.min((planSpent / planLimit) * 100, 100) : 0
              }
              className="h-2"
            />
          )}
        </div>

        {isPermitted([PermissionsKeys.overageUpdate]) &&
          !credits.isOverageEnabled && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>
                When you run out of credit, your apps will stop!
              </AlertDescription>
            </Alert>
          )}

        <PermissionsWrapper
          keys={[PermissionsKeys.subscriptionUpdate]}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.EXTERNALLY_MANAGED]: (
              <Alert variant="info">
                <Info />
                <AlertDescription>
                  To make changes to your plan, please contact our support team.
                </AlertDescription>
              </Alert>
            ),
          }}
        >
          {active.trialType === "user" && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>
                Your {active.plan.display_name} trial finishes on{" "}
                {formatTimestamp(active.renewalDate * 1000)}. Your subscription
                will automatically activate and you'll be charged for the{" "}
                {active.plan.display_name} on this date.
              </AlertDescription>
            </Alert>
          )}
          {active.trialType === "admin" && !hasScheduledUpgradeOrDowngrade && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>
                Your {active.plan.display_name} trial finishes on{" "}
                {formatTimestamp(active.renewalDate * 1000)}. Your subscription
                will automatically cancel on this date.
              </AlertDescription>
            </Alert>
          )}
        </PermissionsWrapper>
      </CardContent>
      <CardFooter className="flex justify-end">
        <PermissionTooltipButton
          permissionKeys={[PermissionsKeys.subscriptionUpdate]}
          additionalDisabled={
            hasScheduledUpgradeOrDowngrade || hasScheduledCancellation
          }
          onClick={() => history.push(Routes.orgManageSubscription)}
        >
          Manage Subscription
        </PermissionTooltipButton>
      </CardFooter>
    </Card>
  );
};
